import { layout } from '@prisjakt/ui-base';
import { TranslateFunc } from '@prisjakt/ui-translate/src/translate-func';
import { Campaign, CampaignBanner, Maybe, Product } from '../../../types';
import { IFilterState, ProductPriceType } from '../types';

export const getHeaderHeight = (isMobile: boolean | undefined) =>
  isMobile
    ? layout.headerHeight.mobile.value
    : layout.headerHeight.desktop.value;

export const getScrollLinkOffset = () => {
  const isMobile = window.innerWidth < 640;
  const extraWhitespace = 20;

  return (
    getHeaderHeight(isMobile) +
    layout.stickyNavigationHeight.value +
    extraWhitespace
  );
};

export const getCurrentCampaign = (
  activeCampaign: Campaign,
  product: Product
) => {
  return (
    activeCampaign &&
    product.campaigns &&
    product.campaigns
      .filter(c => c && String(c.id) === String(activeCampaign.id))
      .map(campaign => {
        return {
          ...campaign,
          name: activeCampaign.name || '',
          path: `${
            activeCampaign.web_uri ? activeCampaign.web_uri.responsive : ''
          }${product.category ? `?category=${product.category.id}` : ''}`,
          media: { ...activeCampaign.media },
        };
      })
      .find(() => true)
  );
};

export const getCampaignPromoBanners = (campaignBanners: {
  active: (CampaignBanner & {
    campaign: Pick<Campaign, 'media'>;
  })[];
}) => {
  return {
    before:
      campaignBanners &&
      campaignBanners.active.filter(b => b && b.type === 'BEFORE_PROMO_BANNER'),
    during:
      campaignBanners &&
      campaignBanners.active.filter(b => b && b.type === 'DURING_PROMO_BANNER'),
  };
};

export const getSimilarProductsCompareLink = (
  similarProducts: Maybe<{ recommendations?: Maybe<any[]> }> | undefined,
  product: Product,
  linkTo: (
    type: string,
    item?: any,
    query?: { [key: string]: any },
    routeSection?: string
  ) => string
) => {
  if (!similarProducts?.recommendations?.length) return null;

  const products = similarProducts.recommendations
    .filter(item => item.product)
    .map(item => item.product);

  return products.length
    ? linkTo('productCompare', {
        category: product.category,
        products: product ? [product, ...products] : products,
      })
    : null;
};

export const blurActiveElement = () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

// Transform some basic information about the product into a DataList-friendly shape to pass into ProductProperties
export const basicInfo = (product: Product, t: TranslateFunc) => {
  if (!product) {
    return [];
  }

  const { brand, category } = product;
  const brandProperties = brand
    ? [
        {
          name: t('product', 'property.brand'),
          value: {
            type: 'string',
            text: brand.name,
            link: brand.pathName,
          },
        },
      ]
    : [];
  const categoryProperties = category
    ? [
        {
          name: t('product', 'property.category'),
          value: {
            type: 'string',
            text: category.name,
            link: category.pathName,
          },
        },
      ]
    : [];

  return [
    ...[
      {
        name: t('product', 'property.product-name'),
        value: { type: 'string', text: product.name },
      },
    ],
    ...brandProperties,
    ...categoryProperties,
  ];
};

export const mapFilterToProductPriceType = (
  filterProperty: FilterPropertyName
) => {
  switch (filterProperty) {
    case 'showInternational':
      return ProductPriceType.INTERNATIONAL;
    case 'showUsedProducts':
      return ProductPriceType.USED_PRODUCT;
    case 'showNotInMintCondition':
      return ProductPriceType.NOT_IN_MINT_CONDITION;
    case 'showPickupOnly':
      return ProductPriceType.PICKUP_ONLY;
    case 'showUnavailable':
      return ProductPriceType.UNAVAILABLE;
    default:
      throw new Error(
        `mapFilterToProductPriceType for type: ${filterProperty} not implemented`
      );
  }
};

type FilterPropertyName = keyof IFilterState;
